package com.arena_web.core.config

import kotlinx.browser.window

object AppConfig {
    private val hostname = window.location.hostname

    // Private LAN IPv4 ranges (RFC1918) — recognised so dev builds opened
    // from a phone on the same network behave as DEVELOPMENT.
    private val lanIpRegex = Regex(
        "^(10\\.|192\\.168\\.|172\\.(1[6-9]|2[0-9]|3[0-1])\\.).*"
    )

    private val isLanDevHost: Boolean =
        hostname.endsWith(".nip.io") || lanIpRegex.matches(hostname)

    val environment: Environment = when {
        hostname == "localhost" || hostname == "127.0.0.1" -> Environment.DEVELOPMENT
        isLanDevHost -> Environment.DEVELOPMENT
        hostname.startsWith("dev.") -> Environment.STAGING
        else -> Environment.PRODUCTION
    }

    private const val PROD_API_URL = "https://api.arenaproplay.com/api"
    private const val DEV_API_URL = "https://api-dev.arenaproplay.com/api"

    // Local dev builds (localhost / LAN) talk to the shared dev backend so
    // testing never touches production data. Flip to true to use a backend
    // running on localhost:8080 instead. Only the DEVELOPMENT branch reads
    // this — the prod/staging hostnames are unaffected.
    private const val USE_LOCAL_BACKEND_IN_DEV = false

    private val cachedApiUrl: String by lazy {
        when (environment) {
            Environment.DEVELOPMENT ->
                getEnv("API_URL") ?: if (USE_LOCAL_BACKEND_IN_DEV) devApiUrl() else DEV_API_URL
            Environment.STAGING -> getEnv("API_URL") ?: DEV_API_URL
            Environment.PRODUCTION -> getEnv("API_URL") ?: PROD_API_URL
        }
    }

    // Resolve dev API URL based on how the page was opened so a phone on the
    // same LAN hits the dev backend on the same host (e.g. nip.io:8080)
    // instead of trying to reach localhost on the device itself.
    private fun devApiUrl(): String =
        if (isLanDevHost) "http://$hostname:8080/api" else "http://localhost:8080/api"

    val apiUrl: String
        get() = cachedApiUrl

    private val cachedWsUrl: String by lazy {
        cachedApiUrl
            .replace("http://", "ws://")
            .replace("https://", "wss://")
            .removeSuffix("/api") + "/ws"
    }

    val wsUrl: String
        get() = cachedWsUrl

    val appName: String = "Arena Pro Play"
    val appVersion: String = "1.0.0"

    val googleClientId: String
        get() = getEnv("GOOGLE_CLIENT_ID") ?: "411703466114-d5lm8q2mgu9vounu1h2172gs0or26d5t.apps.googleusercontent.com"

    // Apple Services ID (not the app bundle id) registered for Sign in with
    // Apple in the developer portal, with the site origins listed as Return
    // URLs / domains.
    val appleClientId: String
        get() = getEnv("APPLE_CLIENT_ID") ?: "com.proplay.arena.web"

    fun getApiUrl(path: String): String {
        val cleanPath = path.removePrefix("/")
        return "$apiUrl/$cleanPath"
    }

    private fun getEnv(key: String): String? {

        val metaTag = window.document.querySelector("meta[name=\"$key\"]")
        if (metaTag != null) {
            return metaTag.getAttribute("content")
        }

        val globalConfig = window.asDynamic().__APP_CONFIG__
        if (globalConfig != null) {
            val configValue = globalConfig[key]
            if (configValue != null && configValue != js("undefined")) {
                return configValue.toString()
            }
        }

        return null
    }
}

enum class Environment {
    DEVELOPMENT,
    STAGING,
    PRODUCTION
}
